import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { isSupabaseConfigured, supabase } from "@/lib/supabase";
import { useAuth } from "@/lib/auth";
import {
  RECEIVE_EPSILON,
  yymmLabel,
} from "@/features/purchase/constants";
import type { PurchaseOrder, PurchaseOrderLine } from "@/features/purchase/types";

export type QualityStatus = "pending" | "passed" | "hold" | "rejected";

export type ReceiveLineInput = {
  purchase_order_line_id: string;
  quantity: number;
  weight: number;
  location_id: string;
  material_grade_id?: string | null;
  shape_size_id?: string | null;
  quality_status: QualityStatus;
  notes?: string;
  thickness?: number | null;
  width?: number | null;
  length?: number | null;
  /** V1 wtmm — stored as inventory_batches.wt_per_m */
  wt_per_m?: number | null;
  /** Override inbound process (default company/PO → PICKLING) */
  last_process_type_id?: string | null;
};

export type GoodsReceiptResult = {
  receipt_id: string;
  receipt_no: string;
  batch_nos: string[];
  po_status: "partial" | "closed";
  /** Vendor AP amount posted for this GRN (taxable + GST) */
  ledger_amount: number;
};

function money(value: unknown) {
  const n = typeof value === "number" ? value : Number(value ?? 0);
  return Number.isNaN(n) ? 0 : n;
}

export function remainingWeight(line: Pick<PurchaseOrderLine, "ordered_weight" | "received_weight">) {
  return Math.max(0, money(line.ordered_weight) - money(line.received_weight));
}

export function useReceiveGoods() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      purchaseOrderId,
      lines,
      notes,
      receivedAt,
    }: {
      purchaseOrderId: string;
      lines: ReceiveLineInput[];
      notes?: string;
      receivedAt?: string;
    }): Promise<GoodsReceiptResult> => {
      if (!isSupabaseConfigured) throw new Error("Supabase is not configured");

      const active = lines.filter((l) => money(l.weight) > 0 || money(l.quantity) > 0);
      if (!active.length) throw new Error("Enter weight or quantity on at least one line");

      for (const line of active) {
        if (!line.location_id?.trim()) throw new Error("Location is required for each received line");
      }

      const payload = active.map((l) => ({
        purchase_order_line_id: l.purchase_order_line_id,
        quantity: money(l.quantity),
        weight: money(l.weight),
        location_id: l.location_id,
        material_grade_id: l.material_grade_id?.trim() || null,
        shape_size_id: l.shape_size_id?.trim() || null,
        quality_status: l.quality_status || "pending",
        notes: l.notes?.trim() || null,
        thickness: l.thickness ?? null,
        width: l.width ?? null,
        length: l.length ?? null,
        wt_per_m: l.wt_per_m ?? null,
        last_process_type_id: l.last_process_type_id?.trim() || null,
      }));

      const { data, error } = await supabase.rpc("receive_goods", {
        p_po_id: purchaseOrderId,
        p_lines: payload,
        p_notes: notes?.trim() || null,
        p_user_id: user?.id ?? null,
        p_received_at: receivedAt ? new Date(receivedAt).toISOString() : null,
      });
      if (error) throw error;

      const raw = data as Record<string, unknown>;
      const batchNos = Array.isArray(raw.batch_nos)
        ? (raw.batch_nos as unknown[]).map(String)
        : [];

      return {
        receipt_id: String(raw.receipt_id),
        receipt_no: String(raw.receipt_no),
        batch_nos: batchNos,
        po_status: raw.po_status === "closed" ? "closed" : "partial",
        ledger_amount: money(raw.ledger_amount),
      };
    },
    onSuccess: async (result, vars) => {
      await queryClient.invalidateQueries({ queryKey: ["purchase_orders"] });
      await queryClient.invalidateQueries({ queryKey: ["purchase_orders", vars.purchaseOrderId] });
      await queryClient.invalidateQueries({ queryKey: ["inventory_batches"] });
      await queryClient.invalidateQueries({ queryKey: ["inventory_movements"] });
      await queryClient.invalidateQueries({ queryKey: ["vendor_ap_movements"] });
      await queryClient.invalidateQueries({ queryKey: ["master_ledger"] });
      await queryClient.invalidateQueries({ queryKey: ["vendors"] });
      const ledgerNote =
        result.ledger_amount > 0
          ? ` · AP ₹${result.ledger_amount.toLocaleString("en-IN")}`
          : "";
      toast.success(
        `${result.receipt_no} — ${result.batch_nos.length} batch(es); PO ${result.po_status}${ledgerNote}`,
      );
    },
    onError: (error: Error) => toast.error(error.message || "Receive failed"),
  });
}

/** Preview next batch number for a prefix (best-effort; not reserved). */
export async function previewBatchNumber(prefix: string, asOf: Date = new Date()): Promise<string> {
  const pfx = prefix.trim().toUpperCase() || "BAT";
  const yymm = yymmLabel(asOf);
  const pattern = `${pfx}-${yymm}-`;
  const { data, error } = await supabase
    .from("inventory_batches")
    .select("batch_no")
    .ilike("batch_no", `${pattern}%`)
    .order("batch_no", { ascending: false })
    .limit(1);
  if (error) throw error;
  const last = data?.[0]?.batch_no as string | undefined;
  const n = last ? Number.parseInt(last.replace(pattern, ""), 10) : 0;
  const next = (Number.isNaN(n) ? 0 : n) + 1;
  return `${pattern}${String(next).padStart(4, "0")}`;
}

export function openReceiveLines(po: PurchaseOrder): PurchaseOrderLine[] {
  return (po.lines ?? []).filter(
    (l) => !l.is_fully_received && remainingWeight(l) > RECEIVE_EPSILON,
  );
}
